import { OfferRequestState } from "./offerRequestState";
import { OfferRequestOrderType } from "./offerRequestOrderType";
import { OfferRequestDeliveryType } from "./offerRequestDeliveryType";
import { OfferRequestPositionDto } from "./offerRequestPositionDto";

/**
 * A request for an individually calculated offer covering large quantities,
 * recurring series or project work. Unlike the automatic offer created in the
 * cart, an offer request is reviewed by a person at Blexon.
 *
 * The request carries its own positions. They are copied from the cart once, when
 * the request is created, and are edited independently from that point on.
 */
export class OfferRequestDto {
	public id: number = 0;
	/** Human readable number shown to the customer, e.g. "SA-1042". */
	public requestNumber: string = "";
	/** Title the customer gave the request. May be empty. */
	public title: string = "";
	public state: OfferRequestState = OfferRequestState.Unknown;
	public createdAt: Date = new Date();
	/** Set once Blexon answered the request. Null while still open. */
	public respondedAt: Date | null = null;

	/** Articles and quantities the offer is requested for. */
	public positions: OfferRequestPositionDto[] = new Array<OfferRequestPositionDto>();

	public orderType: OfferRequestOrderType = OfferRequestOrderType.OneTime;
	/**
	 * Start of the requested completion window as an ISO date. For a recurring
	 * request the window refers to the first delivery.
	 */
	public completionFrom: string | null = null;
	/** End of the requested completion window. See completionFrom. */
	public completionTo: string | null = null;
	/** Expected number of call-offs per year. Recurring requests only. */
	public callOffsPerYear: number | null = null;

	public deliveryType: OfferRequestDeliveryType = OfferRequestDeliveryType.Delivery;
	/** Chosen delivery address. Zero when the customer collects the order. */
	public deliveryAddressId: number = 0;
	/** Free text about delivery or collection, e.g. partial deliveries or time windows. */
	public deliveryNote: string = "";

	/**
	 * Whether a first article inspection is requested at all. Which positions are
	 * sampled and in what quantity is carried by the positions themselves.
	 */
	public wantsFirstArticleInspection: boolean = false;

	/** Free text with anything else that matters for the calculation. */
	public remarks: string = "";

	/** Total of the individual offer. Null until an offer exists. */
	public offerTotal: number | null = null;
	/** Currency of offerTotal, e.g. "CHF". */
	public currency: string = "CHF";
}
